import PropTypes from 'prop-types'
import { Button, Icon, message, Switch, Table } from 'antd'
import React, { useCallback, useEffect, useMemo } from 'react'
import get from 'lodash/get'
import { useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Link, useParams } from 'react-router-dom'
import { useIsAdmin } from '../../../redux/slices/session'
import { useAsync } from '../../../hooks'
import {
  updateOfficeAuthorization,
  useFetchOfficeAuthorizations,
  useOfficeAuthorizations,
  useOfficeAuthorizationsPagination,
} from '../../../redux/slices/office-authorizations'
import { ListError } from '../../Common'
import { toPath } from '../../../routes'
import { formatDOB } from '../../../lib/datetime'
import useExportExcel from '../../../hooks/useExportExcel'
import DownloadLink from '../../Common/DownloadLink'

const useStyles = createUseStyles({
  present: {
    color: 'green',
  },
  notPresent: {
    color: 'orange',
  },
  rejected: {
    textAlign: 'center',
    color: 'red',
  },
})

const BASE_COLUMNS = [
  {
    title: 'Patient',
    dataIndex: 'patient.lastName',
    render(text, preAuthorization) {
      return (
        <Link to={toPath.patients.patient({ id: preAuthorization.patient.id })}>
          {preAuthorization.patient.lastName}
          {', '}
          {preAuthorization.patient.firstName}
        </Link>
      )
    },
  },
  {
    title: 'MRN',
    key: 'MRN',
    dataIndex: 'patient.practiceMrn',
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: 'patient.birthDate',
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: 'device.deviceModel.deviceType.name',
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Billing Date',
    dataIndex: 'billingDate',
    key: 'billingDate',
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, preAuthorization) {
      const {
        insuranceAuthorizationFilePath,
        insuranceAuthorizationNumber,
      } = preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>{insuranceAuthorizationNumber}</span>
        </DownloadLink>
      )
    },
  },
]

export default function OfficeAuthorizations({
  checkDate,
  onPageChange,
  page,
  filters,
  rowSelection,
}) {
  const { practiceId } = useParams()
  const classes = useStyles()
  const isAdmin = useIsAdmin()
  const officeAuthorizations = useOfficeAuthorizations(checkDate)
  const { pageSize, total } = useOfficeAuthorizationsPagination(checkDate)
  const { pending, error, clearError, execute } = useAsync(
    useFetchOfficeAuthorizations(practiceId, page, filters),
    false,
  )
  const dispatch = useDispatch()

  useEffect(() => {
    execute(practiceId, page, filters)
  }, [execute, practiceId, page, filters])

  const handleAuthorizedChange = useCallback(
    async (id, authorized) => {
      try {
        await dispatch(updateOfficeAuthorization(id, { authorized }))
        message.success('Remote authorization successfully updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.authorized',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const filesColumns = [
      {
        title: 'PDF',
        dataIndex: 'hasReportFile',
        key: 'hasReportFile',
        align: 'center',
        render(text, { hasReportFile }) {
          return (
            <>
              {hasReportFile === null ? (
                <span>-</span>
              ) : (
                <Icon
                  type={`${hasReportFile ? 'check' : 'close'}-circle`}
                  className={classes[hasReportFile ? 'present' : 'notPresent']}
                />
              )}
            </>
          )
        },
      },
      {
        title: 'Full',
        dataIndex: 'hasReportRawFile',
        key: 'hasReportRawFile',
        align: 'center',
        render(text, { hasReportRawFile }) {
          return (
            <>
              {hasReportRawFile === null ? (
                <span>-</span>
              ) : (
                <Icon
                  type={`${hasReportRawFile ? 'check' : 'close'}-circle`}
                  className={
                    classes[hasReportRawFile ? 'present' : 'notPresent']
                  }
                />
              )}
            </>
          )
        },
      },
    ]

    return [
      ...BASE_COLUMNS,
      {
        title: 'Authorized',
        key: 'authorized',
        render: (text, record) => {
          return (
            <Switch
              checked={record.authorized}
              checkedChildren="Authorized"
              unCheckedChildren="Not&nbsp;Authorized"
              onChange={authorized =>
                handleAuthorizedChange(record.id, authorized)
              }
            />
          )
        },
      },
      ...(isAdmin ? filesColumns : []),
      {
        title: 'Actions',
        key: 'actions',
        render(text, preAuthorization) {
          return (
            <Link
              to={`${toPath.billing.officeAuthorizations.officeAuthorization({
                id: preAuthorization.id,
              })}?practiceId=${practiceId}`}
            >
              <Button type="link" icon="file-search" />
            </Link>
          )
        },
      },
    ]
  }, [classes, handleAuthorizedChange, isAdmin, practiceId])

  const [exportingExcel, exportExcel] = useExportExcel(
    `practices/${practiceId}/in-office-authorizations.xlsx?check_date=${checkDate}`,
  )

  return (
    <>
      {error && <ListError description={error.message} onClose={clearError} />}
      <Table
        size="middle"
        columns={columns}
        dataSource={officeAuthorizations}
        rowKey="id"
        loading={pending}
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize,
          onChange: onPageChange,
          total,
        }}
      />
      {Boolean(officeAuthorizations.length) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon="file-excel"
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </>
  )
}

OfficeAuthorizations.propTypes = {
  checkDate: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  rowSelection: PropTypes.shape({}),
  page: PropTypes.number,
  filters: PropTypes.shape({ q: PropTypes.string }),
}

OfficeAuthorizations.defaultProps = {
  page: 1,
  filters: {},
  rowSelection: null,
}
